import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X } from "lucide-react";
import { useLang, LANGS, tr } from "@/lib/i18n";
import { mediaUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { txt, safeHref } from "@/lib/siteDefaults";

export function SiteHeader({ header, device, edit, lang: langProp, onNavigate }) {
  const ctx = useLang();
  const lang = langProp || ctx.lang;
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const compact = device !== "desktop";
  const nav = (header.nav || []).filter((n) => !n.hidden || edit).map((n) => ({ ...n, href: safeHref(n.href) }));
  const selected = edit?.selected?.scope === "header";
  const go = (e, href) => {
    setOpen(false);
    if (edit) { e.preventDefault(); return; }
    if (/^(https?:|mailto:|tel:)/i.test(href)) return;
    e.preventDefault(); if (href !== "#") onNavigate(href);
  };
  const isActive = (href) => (href === "/site" ? loc.pathname === "/site" || loc.pathname === "/site/" : loc.pathname.startsWith(href));
  const logo = header.logo_url
    ? <img src={mediaUrl(header.logo_url)} alt="logo" style={{ height: header.logo_height || 48, transform: `translate(${header.logo_offset_x || 0}px, ${header.logo_offset_y || 0}px)` }} data-testid="site-logo-img" />
    : <Logo />;
  return (
    <header onClick={edit ? (e) => { e.stopPropagation(); edit.select({ scope: "header" }); } : undefined}
      className={`${edit ? "relative" : "sticky top-0"} z-50 backdrop-blur border-b border-gold/20`}
      style={{ background: header.bg, outline: selected ? "3px solid #C5A059" : undefined, outlineOffset: -3 }} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between" style={{ height: header.height || 80 }}>
        <a href="/site" onClick={(e) => go(e, "/site")}>{logo}</a>
        {!compact && (
          <nav className="flex items-center gap-6">
            {nav.map((n) => (
              <a key={n.id} href={n.href} onClick={(e) => go(e, n.href)} className="text-sm tracking-wide transition-colors hover:opacity-100"
                style={{ color: isActive(n.href) ? header.active : header.text, opacity: n.hidden ? 0.35 : isActive(n.href) ? 1 : 0.85 }} data-testid={`site-nav-${n.href.split("/").pop() || "home"}`}>
                {txt(n.label, lang)}
              </a>
            ))}
          </nav>
        )}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1" data-testid="lang-switch">
            {LANGS.map((l) => (
              <button key={l.code} onClick={(e) => { if (edit) return; e.stopPropagation(); ctx.setLang(l.code); }}
                className={`px-2 py-1 text-xs rounded-md transition-colors ${lang === l.code ? "bg-gold text-forest-deep font-semibold" : "text-cream/70 hover:text-white"}`} data-testid={`lang-${l.code}`}>
                {l.label}
              </button>
            ))}
          </div>
          {!compact && !edit && <Link to="/login" className="text-xs text-cream/60 hover:text-gold transition-colors">{tr(lang, "staff_login")}</Link>}
          {compact && <button className="text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">{open ? <X /> : <MenuIcon />}</button>}
        </div>
      </div>
      {compact && open && (
        <nav className="border-t border-gold/20 px-4 py-4 flex flex-col gap-3" style={{ background: header.bg }}>
          {nav.map((n) => <a key={n.id} href={n.href} onClick={(e) => go(e, n.href)} style={{ color: header.text }} className="py-1">{txt(n.label, lang)}</a>)}
          {!edit && <Link to="/login" className="text-gold py-1">{tr(lang, "staff_login")}</Link>}
        </nav>
      )}
    </header>
  );
}
